import { PostHogProvider, usePostHog } from "posthog-js/react";
import type { CaptureResult } from "posthog-js";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useSnapshot } from "valtio";
import { auth } from "./state";
import { getEnvVar } from "./utils";

type ConsentCategory = "necessary" | "functional" | "analytics" | "performance" | "advertisement";

type CookieYesWindow = typeof window & {
    getCkyConsent?: () => { categories?: Record<string, boolean> };
};

function hasConsent(category: ConsentCategory): boolean {
    return (window as CookieYesWindow).getCkyConsent?.().categories?.[category] === true;
}

function subscribeConsent(category: ConsentCategory, onChange: (granted: boolean) => void): () => void {
    const onLoad = () => onChange(hasConsent(category));

    const onUpdate = (event: Event) => {
        const detail = (event as CustomEvent<{ accepted?: string[]; rejected?: string[] }>).detail;
        if (detail?.accepted?.includes(category)) onChange(true);
        else if (detail?.rejected?.includes(category)) onChange(false);
    };

    document.addEventListener("cookieyes_banner_load", onLoad);
    document.addEventListener("cookieyes_consent_update", onUpdate);

    let tries = 0;
    const poll = window.setInterval(() => {
        if ((window as CookieYesWindow).getCkyConsent) {
            onChange(hasConsent(category));
            window.clearInterval(poll);
        } else if (tries++ > 50) {
            window.clearInterval(poll);
        }
    }, 250);

    return () => {
        document.removeEventListener("cookieyes_banner_load", onLoad);
        document.removeEventListener("cookieyes_consent_update", onUpdate);
        window.clearInterval(poll);
    };
}

function useConsent(category: ConsentCategory): boolean {
    const [granted, setGranted] = useState(() => hasConsent(category));
    useEffect(() => subscribeConsent(category, setGranted), [category]);
    return granted;
}

/* -------------------------------------------------------------------------- */
/*  LinkedIn
/* -------------------------------------------------------------------------- */

const LINKEDIN_PARTNER_ID = "10007714";

type LinkedInWindow = typeof window & {
    _linkedin_partner_id?: string;
    _linkedin_data_partner_ids?: string[];
    lintrk?: ((action: unknown, payload: unknown) => void) & { q?: unknown[][] };
};

function injectLinkedInInsightTag() {
    if (document.getElementById("linkedin-insight-tag")) return;
    const w = window as LinkedInWindow;
    w._linkedin_partner_id = LINKEDIN_PARTNER_ID;
    w._linkedin_data_partner_ids = w._linkedin_data_partner_ids ?? [];
    w._linkedin_data_partner_ids.push(LINKEDIN_PARTNER_ID);
    if (!w.lintrk) {
        const lintrk = ((action: unknown, payload: unknown) => {
            lintrk.q.push([action, payload]);
        }) as ((action: unknown, payload: unknown) => void) & { q: unknown[][] };
        lintrk.q = [];
        w.lintrk = lintrk;
    }
    const script = document.createElement("script");
    script.id = "linkedin-insight-tag";
    script.async = true;
    script.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
    document.head.appendChild(script);
}

export function useLinkedInInsightTag() {
    useEffect(() => {
        return subscribeConsent("advertisement", (granted) => {
            if (granted) injectLinkedInInsightTag();
        });
    }, []);
}

/* -------------------------------------------------------------------------- */
/*  PostHog
/* -------------------------------------------------------------------------- */

function posthogEnv() {
    const apiKey = getEnvVar("VITE_POSTHOG_API_KEY");
    const apiHost = getEnvVar("VITE_POSTHOG_API_HOST");
    const uiHost = getEnvVar("VITE_POSTHOG_UI_HOST");
    return apiKey && apiHost && uiHost ? { apiKey, apiHost, uiHost } : null;
}

function isInternalUser(email: string | null): boolean {
    return email?.endsWith("@lumidb.com") || window.location.hostname === "localhost";
}

function PostHogConsent({ children, sessionRecording }: { children: ReactNode; sessionRecording: boolean }) {
    const posthog = usePostHog();
    const consented = useConsent("analytics");
    const { user, email, orgSlug, orgRole } = useSnapshot(auth.state);

    useEffect(() => {
        posthog.set_config({
            persistence: consented ? "localStorage+cookie" : "memory",
        });
    }, [posthog, consented]);

    useEffect(() => {
        if (consented && sessionRecording) {
            posthog.startSessionRecording();
        } else {
            posthog.stopSessionRecording();
        }
    }, [posthog, consented, sessionRecording]);

    useEffect(() => {
        if (!consented || !user) return;

        posthog.identify(user, {
            org_slug: orgSlug,
            org_role: orgRole,
            is_internal_user: isInternalUser(email),
            email,
        });
    }, [posthog, consented, user, email, orgSlug, orgRole]);

    return <>{children}</>;
}

export function TrackingProvider({
    children,
    sessionRecording = false,
}: {
    children: ReactNode;
    sessionRecording?: boolean;
}) {
    const env = posthogEnv();
    if (!env) return <>{children}</>;

    return (
        <PostHogProvider
            apiKey={env.apiKey}
            options={{
                api_host: env.apiHost,
                ui_host: env.uiHost,
                defaults: "2025-05-24",
                persistence: "memory",
                before_send: (event: CaptureResult | null) => {
                    return hasConsent("analytics") ? event : null;
                },
                disable_session_recording: true,
                session_recording: {
                    // Low FPS and quality because capture uses `toDataUrl` which can be slow.
                    captureCanvas: {
                        canvasFps: 0.5,
                        canvasQuality: "0.05",
                    },
                },
            }}
        >
            <PostHogConsent sessionRecording={sessionRecording}>{children}</PostHogConsent>
        </PostHogProvider>
    );
}

export type EventProperties = Record<string, string | number | boolean | null | undefined>;

export function useAnalytics() {
    const posthog = usePostHog();

    const trackEvent = useCallback(
        (eventName: string, properties?: EventProperties) => {
            if (!posthog.__loaded) return;
            posthog.capture(eventName, {
                org_slug: auth.state.orgSlug,
                org_role: auth.state.orgRole,
                ...properties,
            });
        },
        [posthog],
    );

    return { trackEvent };
}
